import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Alert, Paper, CircularProgress, Divider } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import KeyIcon from '@mui/icons-material/Key';
import { requestOtp, verifyOtp, claimPhone, resetPhone } from '../api/whatsapp';

const OtpVerify = ({ account, onComplete, onChangeNumber, otpAlreadySent = false }) => {
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(otpAlreadySent ? 'otp' : 'send');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState({ sms: false, voice: false });

  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimPhoneNumberId, setClaimPhoneNumberId] = useState('');
  const [claimWabaId, setClaimWabaId] = useState('');
  const [claimError, setClaimError] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleChangeNumber = async () => {
    setResetting(true);
    try {
      await resetPhone();
      if (onChangeNumber) onChangeNumber();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset phone number');
    }
    setResetting(false);
  };

  const handleSendOtp = async (method = 'SMS') => {
    setError('');
    if (step === 'send') setLoading(true);
    else setRetrying(p => ({ ...p, [method.toLowerCase()]: true }));
    try {
      await requestOtp(method);
      if (step === 'send') setStep('otp');
      else setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    }
    if (step === 'send') setLoading(false);
    else setRetrying(p => ({ ...p, [method.toLowerCase()]: false }));
  };

  const handleVerifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await verifyOtp(otp);
      if (res.status === 'active' || res.status === 'otp_verified') onComplete();
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    }
    setLoading(false);
  };

  const handleClaimPhone = async () => {
    if (!claimPhoneNumberId.trim() || !claimWabaId.trim()) {
      setClaimError('Both Phone Number ID and WABA ID are required');
      return;
    }
    setClaimError('');
    setClaimLoading(true);
    try {
      await claimPhone(claimPhoneNumberId.trim(), claimWabaId.trim());
      onComplete();
    } catch (err) {
      setClaimError(err.response?.data?.message || 'Failed to claim phone number');
    }
    setClaimLoading(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f0f2f5', p: 3 }}>
      <Paper sx={{ maxWidth: 480, width: '100%', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#075e54', p: 3, color: '#fff', textAlign: 'center' }}>
          <LockIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Verify Your Number</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {step === 'send' ? 'Send OTP to your WhatsApp Business number' : 'Enter the code sent to your number'}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}
          {claimError && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{claimError}</Alert>}

          <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', borderRadius: '16px', border: '1px solid #bbf7d0', mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#075e5418', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PhoneIcon sx={{ color: '#075e54', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>WhatsApp Business Number</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#075e54', fontSize: 16 }}>
                {account?.whatsappBusinessNumber || 'Not available'}
              </Typography>
            </Box>
          </Box>

          {step === 'send' && (
            <Box>
              <Button variant="contained" fullWidth onClick={() => handleSendOtp('SMS')} disabled={loading}
                sx={{
                  borderRadius: '14px', textTransform: 'none', py: 1.4, fontSize: 15,
                  bgcolor: '#25d366', color: '#fff', fontWeight: 700,
                  '&:hover': { bgcolor: '#1da851' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8' }
                }}>
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send OTP via SMS'}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="text" onClick={handleChangeNumber} disabled={resetting}
                  sx={{ textTransform: 'none', color: '#64748b', fontSize: 13 }}>
                  {resetting ? 'Resetting...' : 'Wrong number? Change phone number'}
                </Button>
              </Box>
            </Box>
          )}

          {step === 'otp' && !showClaimForm && (
            <Box>
              <TextField label="Enter 6-digit OTP" fullWidth required
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 } }}
                sx={{ mb: 2 }}
                InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
                InputLabelProps={{ sx: { fontWeight: 500 } }}
              />
              <Button variant="contained" fullWidth onClick={handleVerifyOtp} disabled={loading || otp.length < 6}
                sx={{
                  borderRadius: '14px', textTransform: 'none', py: 1.4, fontSize: 15,
                  bgcolor: '#075e54', fontWeight: 700,
                  '&:hover': { bgcolor: '#054d44' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8' }
                }}>
                {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify OTP'}
              </Button>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button fullWidth variant="outlined" onClick={() => handleSendOtp('SMS')} disabled={retrying.sms}
                  sx={{ textTransform: 'none', borderRadius: '12px', borderColor: '#3b82f6', color: '#3b82f6' }}>
                  {retrying.sms ? <CircularProgress size={16} /> : 'Resend SMS'}
                </Button>
                <Button fullWidth variant="outlined" onClick={() => handleSendOtp('VOICE')} disabled={retrying.voice}
                  sx={{ textTransform: 'none', borderRadius: '12px', borderColor: '#25d366', color: '#25d366' }}>
                  {retrying.voice ? <CircularProgress size={16} sx={{ color: '#25d366' }} /> : 'Voice Call'}
                </Button>
              </Box>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="text" onClick={handleChangeNumber} disabled={resetting}
                  sx={{ textTransform: 'none', color: '#64748b', fontSize: 13 }}>
                  {resetting ? 'Resetting...' : 'Wrong number? Change phone number'}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>OR</Typography>
              </Divider>

              <Button fullWidth variant="text" onClick={() => setShowClaimForm(true)}
                sx={{ textTransform: 'none', color: '#075e54', fontWeight: 600 }}>
                Already have Phone Number ID? Activate directly
              </Button>
            </Box>
          )}

          {showClaimForm && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <KeyIcon sx={{ color: '#075e54' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#075e54' }}>
                  Activate via WhatsApp Manager
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                Go to <b>WhatsApp Manager</b> → <b>Phone Numbers</b> → copy your Phone Number ID and WABA ID, then paste below.
              </Typography>

              <TextField label="Phone Number ID" fullWidth required
                value={claimPhoneNumberId}
                onChange={e => setClaimPhoneNumberId(e.target.value)}
                placeholder="e.g. 1262349863618408"
                InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
                InputLabelProps={{ sx: { fontWeight: 500 } }}
                sx={{ mb: 2 }}
              />
              <TextField label="WABA ID" fullWidth required
                value={claimWabaId}
                onChange={e => setClaimWabaId(e.target.value)}
                placeholder="e.g. 1004781542329612"
                InputProps={{ sx: { borderRadius: '14px', bgcolor: '#f8fafc' } }}
                InputLabelProps={{ sx: { fontWeight: 500 } }}
                sx={{ mb: 2 }}
              />

              <Button variant="contained" fullWidth onClick={handleClaimPhone} disabled={claimLoading || !claimPhoneNumberId.trim() || !claimWabaId.trim()}
                sx={{
                  borderRadius: '14px', textTransform: 'none', py: 1.4, fontSize: 15,
                  background: 'linear-gradient(135deg, #075e54, #0a8a7a)', fontWeight: 700,
                  '&:hover': { background: 'linear-gradient(135deg, #054d44, #087566)' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8' },
                }}>
                {claimLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify & Activate Account'}
              </Button>

              <Button fullWidth variant="text" onClick={() => setShowClaimForm(false)} sx={{ mt: 1, textTransform: 'none', color: '#64748b' }}>
                Back to OTP verification
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default OtpVerify;
